"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { MapPin, Film } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const locations = [{
  id: 1,
  name: "Lisbon Waterfront",
  location: "Lisbon, Portugal",
  description: "Historic waterfront district with cobblestone streets and colorful buildings, perfect for commercials and European period pieces.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1520135/img/lisbon_waterfront_portugal.png",
  category: "Urban"
}, {
  id: 2,
  name: "Berlin Studio Complex",
  location: "Berlin, Germany",
  description: "State-of-the-art production facility with soundstages and modern equipment, ideal for high-budget films and series.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1520135/img/berlin_studio_complex_germany.png",
  category: "Studio"
}, {
  id: 3,
  name: "Austrian Alps",
  location: "Tyrol, Austria",
  description: "Breathtaking mountain scenery with alpine villages and pristine landscapes, excellent for adventure and travel content.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1520135/img/austrian_alps_tyrol.png",
  category: "Natural"
}, {
  id: 4,
  name: "Prague Castle District",
  location: "Prague, Czech Republic",
  description: "Medieval architecture and historic squares with stunning Gothic and Baroque buildings, great for period dramas and fantasy films.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1520135/img/prague_castle_district_czech.png",
  category: "Historic"
}, {
  id: 5,
  name: "Amsterdam Canals",
  location: "Amsterdam, Netherlands",
  description: "Iconic canal houses and bridge-lined waterways offering unique European charm for lifestyle and fashion shoots.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1520135/img/amsterdam_canals_netherlands.png",
  category: "Urban"
}, {
  id: 6,
  name: "Bavarian Castle Grounds",
  location: "Bavaria, Germany",
  description: "Majestic castle estates surrounded by forests and lakes, providing fairy-tale backdrops for high-end productions.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1520135/img/bavarian_castle_grounds_germany.png",
  category: "Historic"
}];
export default function FilmLocationShowcase() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`text-center mb-12 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-primary/10 rounded-full">
            <Film className="size-5 text-foreground" />
            <span className="text-sm font-semibold text-foreground">Film Locations</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-4">
            Our Production Locations
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore our curated collection of stunning film locations and professional set designs, 
            each offering unique visual possibilities for your next project.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((location, i) => <Card key={location.id} data-index={i} className={`group overflow-hidden border border-border bg-card hover:shadow-medium hover:-translate-y-1 transition-all duration-300 ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{
          transitionDelay: `${i * 100}ms`
        }}>
              <div className="relative overflow-hidden">
                <img src={location.image} alt={location.name} className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute top-4 right-4 px-3 py-1 bg-background/90 backdrop-blur-sm rounded-full text-xs font-semibold text-foreground">
                  {location.category}
                </div>
              </div>
              
              <CardHeader>
                <CardTitle className="text-xl group-hover:text-primary-dark transition-colors duration-300">
                  {location.name}
                </CardTitle>
                <CardDescription className="flex items-center gap-1.5 text-sm">
                  <MapPin className="size-4" />
                  {location.location}
                </CardDescription>
              </CardHeader>

              <CardContent className="px-6">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {location.description}
                </p>
              </CardContent>
            </Card>)}
        </div>

        <div className="mt-12 text-center">
          <Link to="#contact" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 h-10 px-6 has-[>svg]:px-4 shadow-medium hover:shadow-strong">
            Contact Us for Location Scouting
          </Link>
        </div>
      </div>
    </section>;
}